import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'clsx';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentLoader from 'react-content-loader';

import Currencies from '../../../../../utils/Currencies';
import DealPrice from '../../../../DealPrice.react';
import NegotiatedRate from '../../../../general/NegotiatedRate.react';
import ProfileStore from '../../../../../stores/ProfileStore';
import PriceBreakdown from 'arbitrip-common/client/utils/PriceBreakdown';
import Config from 'arbitrip-common/client/utils/Config';
import {
    getMaxPointsForDeal,
    getPointsDebugTooltipContent,
    getTotalPriceInPoints,
} from 'arbitrip-common/client/utils/PointsUtils';



function WrapperDealPriceContainer({
	deal,
	profile,
	arbitripPointsApplied,
	busyApplyingPoints,
	showPricesWithPoints,
	points,
	nights,
	pointsDiscountValue,
	priceBeforeDiscount,
	children,
	totalPriceAfterDiscount,
	anonPointsOnly,
	anonPointsExRate,
}) {
	const { t } = useTranslation();

	const faultDeal = deal?.faultDeal;
	const company_name = _.get(profile, 'company.name', null);
	if (busyApplyingPoints) {
		return (
			<div className="deal-price-container">
				<div className="inner-container">
					<ContentLoader width={124} height={32} viewBox="0 0 160 32">
						<rect x="0" y="0" rx="0" yx="0" width="160" height="32" />
					</ContentLoader>
				</div>
			</div>
		);
	}

	return (
		<div className="deal-price-container">
			<div className="inner-container">
				{ProfileStore.hasArbitripPoints() && arbitripPointsApplied && (
					<div className="old-price">
						<span>
							{Currencies.getPriceWithDisplayCurrencyByPrecision(
								faultDeal ? deal.previousPricePerNight : deal.pricePerNight,
								profile.display_currency_exchange_rate,
								profile.display_currency,
								0,
							)}
						</span>
					</div>
				)}
				{!!priceBeforeDiscount && (
					<div className="old-price">
						<span>
							{Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
								priceBeforeDiscount,
								profile.display_currency_exchange_rate,
								profile.display_currency,
							)}
						</span>
					</div>
				)}
				<div className="deal-price">
					<NegotiatedRate
						companyName={company_name}
						show={!!deal.negotiated_rate}
						rawCode={deal.original_negotiated_rate || ''}
						code={deal.negotiated_rate || ''}
						codeDisplayName={deal.negotiated_rate_display_name || ''}
					/>
					{(showPricesWithPoints || anonPointsOnly) && (
						<>
							{!anonPointsOnly && (
								<div className="deal-points-text">
									{t('MaxPointsToRedeem')}
								</div>
							)}
							<div className="deal-points" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span className="deal-points">
                                    {anonPointsOnly
                                        ? getTotalPriceInPoints(deal, profile, anonPointsExRate).toLocaleString('en-US')
                                        : getMaxPointsForDeal(deal, profile, points).toLocaleString('en-US')}
                                </span>
								<div className='deal-points__labels-container'>
									<span className='deal-points-text'>{t('Points')}</span>
									{Config.dev_mode && !anonPointsOnly && (
										<Tooltip
											title={getPointsDebugTooltipContent(deal, profile, points, pointsDiscountValue, nights, getMaxPointsForDeal)}
											placement="top"
											arrow
										>
											<InfoOutlinedIcon style={{ fontSize: '14px', cursor: 'help', color: '#666' }} />
										</Tooltip>
									)}
								</div>
							</div>
                            {!anonPointsOnly &&
                                (
									<span className='rest-cash-amount'>
										+ {Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
											totalPriceAfterDiscount,
											profile.display_currency_exchange_rate,
											profile.display_currency,
										)}
									</span>
								)}
						</>
					)}
					{!showPricesWithPoints && !anonPointsOnly && <div className="price-per-night">{children}</div>}
				</div>
			</div>
		</div>
	);
}

function DealPriceContainer({
	deal,
	profile,
	arbitripPointsApplied,
	contractClientCompanyId,
	busyApplyingPoints,
	showPricesWithPoints,
	points,
	nights,
	pointsDiscountValue,
	expediaTotalPriceAfterDiscount,
	totalPriceAfterDiscount,
	priceBeforeDiscount,
	anonPointsOnly,
	anonPointsExRate,
}) {
	const { t } = useTranslation();

	const faultDeal = deal?.faultDeal;
	const arbitrip_points_reduction = arbitripPointsApplied ? _.get(profile, 'bank.balance', 0) : 0;

	const wrapperProps = {
		deal,
		profile,
		arbitripPointsApplied,
		busyApplyingPoints,
		showPricesWithPoints,
		points,
		nights,
		pointsDiscountValue,
		priceBeforeDiscount,
		totalPriceAfterDiscount,
		anonPointsOnly,
		anonPointsExRate,
	};

	if (PriceBreakdown.isBreakdownPrice({ profile, deal })) {
		return (
			<WrapperDealPriceContainer {...wrapperProps}>
				<span data-testid="deal-price" className="deal-price">
					{Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
						expediaTotalPriceAfterDiscount,
						profile.display_currency_exchange_rate,
						profile.display_currency,
					)}
				</span>
			</WrapperDealPriceContainer>
		);
	}
	
	if (PriceBreakdown.isShowTotalPrice({ profile })) {
		return (
			<WrapperDealPriceContainer {...wrapperProps}>
				<span data-testid="deal-price">
					{Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
						totalPriceAfterDiscount,
						profile.display_currency_exchange_rate,
						profile.display_currency,
					)}
				</span>
			</WrapperDealPriceContainer>
		);
	}

	const previousPrice =
		Config.dev_mode && deal.previousPricePerNight
			? t('DevMode.OldPricePerNight', { price: deal.previousPricePerNight })
			: '';
	const originalCurrencyPrice =
		Config.dev_mode && deal.currency !== profile.display_currency
			? t('DevMode.OriginalCurrencyPrice', {
					currency: deal.currency,
					price: deal.pricePerNight,
				})
			: '';

	if (profile.agent) {
		return (
			<WrapperDealPriceContainer {...wrapperProps}>
				<DealPrice
					previousPrice={previousPrice}
					displayTotalPrice={true}
					supplier={deal.supplier_name}
					currency={deal.currency}
					price={deal.pricePerNight}
					totalPrice={deal.totalPrice}
					netPrice={deal.net_price_total}
					commission={deal.commission_total}
					dealId={deal.id}
					faultDeal={faultDeal}
					profile={profile}
					contractClientCompanyId={contractClientCompanyId}
				/>
			</WrapperDealPriceContainer>
		);
	}

	return (
		<WrapperDealPriceContainer {...wrapperProps}>
			<span
				title={
					Config.dev_mode &&
					(previousPrice + (deal.currency !== profile.display_currency)
						? ' | ' + originalCurrencyPrice
						: '')
				}
				className={classNames(faultDeal && 'fault-deal')}
			>
				<span data-testid="deal-price">
					{Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
						(faultDeal
							? deal.previousPricePerNight
							: deal.pricePerNight) - arbitrip_points_reduction - (nights > 0 ? pointsDiscountValue / nights : 0),
						profile.display_currency_exchange_rate,
						profile.display_currency,
					)}
				</span>
			</span>
		</WrapperDealPriceContainer>
	);
}

DealPriceContainer.propTypes = {
	deal: PropTypes.object.isRequired,
	profile: PropTypes.object.isRequired,
	arbitripPointsApplied: PropTypes.bool,
	contractClientCompanyId: PropTypes.any,
	busyApplyingPoints: PropTypes.bool,
	showPricesWithPoints: PropTypes.bool,
	points: PropTypes.object,
	nights: PropTypes.number.isRequired,
	pointsDiscountValue: PropTypes.number,
	expediaTotalPriceAfterDiscount: PropTypes.number,
	totalPriceAfterDiscount: PropTypes.number,
	priceBeforeDiscount: PropTypes.number,
	anonPointsOnly: PropTypes.bool,
	anonPointsExRate: PropTypes.number,
};

export default DealPriceContainer;
